import {AfterViewInit, Component, ElementRef, forwardRef, Host, Injector, Input, OnInit, Optional, Renderer2, SkipSelf, ViewChild} from '@angular/core';
import {ControlContainer, ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {ErrorMessageComponent} from '../../error-message/error-message.component';
import {ValueAccessorAbstract} from '@wba-ui-module/components/formfields/commons/value-accessor.abstract';
import {EventUtils} from '@wba/utils/event.utils';
import {FormUtils} from '@wba/utils/form.utils';

/**
 * wba-input-suggestion ist eine Mischung aus Type-Ahead und Selectbox, die es in der DPL3.4 gar nicht so gibt.
 * Es könnte sein, dass es sinnvoll ist an manchen der eingesetzten Stellen ein wba-input-select zu nehmen und
 * den Rest auf Text oder Type Ahead umzustellen.
 */
@Component({
    selector: 'wba-input-suggestion',
    templateUrl: './input-suggestion.component.html',
    styleUrls: ['./input-suggestion.component.scss'],
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputSuggestionComponent),
            multi: true
        }],
    standalone: false
})
export class InputSuggestionComponent extends ValueAccessorAbstract implements ControlValueAccessor, OnInit, AfterViewInit {

  @Input() id: string;
  @Input() name: string;
  @Input() label: string;
  @Input() pattern: string;
  @Input() placeholder = 'Bitte eingeben oder wählen';
  @Input() inputType = 'text';
  @Input() maxLength = null;
  @Input() required: boolean;
  @Input() suggestedItems: string[];
  @Input() hinweis: { target: string, modal: string };  // kann eigentlich weg, weil es keine Hinweistexte im Dialog gibt
  public showSuggestedItems: boolean;
  @ViewChild('input') el: ElementRef;
  @ViewChild('listElement') listElement: ElementRef;
  @ViewChild(ErrorMessageComponent) errorMessageComponent: ErrorMessageComponent;

  constructor(@Optional() @Host() @SkipSelf() private readonly controlContainer: ControlContainer,
              private readonly elementRef: ElementRef,
              private readonly eventUtils: EventUtils,
              private readonly renderer: Renderer2,
              protected injector: Injector) {
    super(injector);
  }

  get value(): string {
    return this.innerValue;
  }
  set value(v: string) {
    if (v !== this.innerValue) {
      this.innerValue = v.replaceAll(/<[^>]*>/g, '');
      this.onChangeCallback(this.innerValue);
      // this occurs issues with IE
      // this.showSuggestedItems = true;
      this.resetActiveElement();
    }
  }

  ngOnInit() {
    super.ngOnInit();
    if (this.controlContainer) {
      if (this.formControlName) {
        this.control = this.controlContainer.control.get(this.formControlName) as FormControl;
      }
    }
  }

  ngAfterViewInit() {
    // Workaround for IE:
    // Chrome works with blur and focusout properly
    // Firefox works only with blur
    // IE works only with focusout
    const event = navigator.userAgent.toLowerCase().includes('firefox') ? 'blur' : 'focusout';
    (this.el?.nativeElement as HTMLInputElement)?.addEventListener(event, this.onBlur.bind(this));
  }

  // Set touched on blur
  onBlur(event) {
    if (this.value?.length) {
      this.writeValue(FormUtils.formValueSingleWhitespaces(this.value));
    }

    if (this.errorMessageComponent) {
      this.el.nativeElement.setAttribute('aria-describedby', this.errorMessageComponent.getErrorMessageId());
      this.errorMessageComponent.isVisible = false;
    }

    if (this.suggestedItems.length === 0 || this.listElement && (event.relatedTarget !== this.listElement.nativeElement)) {
      this.showSuggestedItems = false;
      this.onTouchedCallback();
    }

    this.eventUtils.focusOut$.next(this.elementRef);
  }

  onFocus() {
    this.showSuggestedItems = true;
    if (this.suggestedItems.length === 0) {
      this.errorMessageComponent.isVisible = true;
    }
  }

  setSuggestedItem(item) {
    if (this.listElement) {
      this.value = item;
      (this.listElement.nativeElement as HTMLElement).blur();
      this.showSuggestedItems = false;
      this.onTouchedCallback();
      this.eventUtils.focusOut$.next(this.elementRef);
    }
  }

  onKeydown(event) {
    const validKeys = ['Enter', 'ArrowDown', 'Down', 'ArrowUp', 'Up'];
    if (!validKeys.includes(event.key)) {
      this.resetElement();
      return;
    }

    this.checkActiveElement(event);
  }

  private resetElement() {
    if (this.el.nativeElement.hasAttribute('aria-activedescendant')) {
      this.el.nativeElement.removeAttribute('aria-activedescendant');
    }
  }

  private checkActiveElement(event) {
    if (this.listElement && this.suggestedItems.length > 0) {
      let activeElement = this.listElement.nativeElement.getElementsByClassName('geo-selectable active');
      if (activeElement.length === 0) {
        activeElement = this.listElement.nativeElement.getElementsByClassName('geo-selectable')[0];
      } else {
        activeElement = activeElement[0];
        activeElement.classList.remove('active');
        activeElement.setAttribute('aria-selected', 'false');
        if (event.key === 'Enter') {
          this.setSuggestedItem(activeElement.innerText);
        } else if (event.key === 'ArrowDown' || event.key === 'Down') {
          activeElement = this.getNextSelectableElement(activeElement);
        } else if (event.key === 'ArrowUp' || event.key === 'Up') {
          activeElement = this.getPreviousSelectableElement(activeElement);
        }
      }
      this.setActiveElement(activeElement);
    }
  }

  private setActiveElement(activeElement) {
    if (activeElement) {
      activeElement.classList.add('active');
      this.el.nativeElement.setAttribute('aria-activedescendant', activeElement.getAttribute('id'));
      activeElement.setAttribute('aria-selected', 'true');
    }
  }

  private readonly getNextSelectableElement = (activeElement) => {
    return activeElement.nextElementSibling
      ? activeElement.nextElementSibling : activeElement;
  };

  private readonly getPreviousSelectableElement = (activeElement) => {
    return activeElement.previousElementSibling
      ? activeElement.previousElementSibling : activeElement;
  };

  private readonly resetActiveElement = () => {
    if (!this.listElement) {
      return false;
    }
    const activeElement = this.listElement.nativeElement.getElementsByClassName('geo-selectable active');
    if (activeElement.length > 0) {
      activeElement[0].classList.remove('active');
    }
    return true;
  };
}
